import type { SizeOptionWithEdd } from '@/features/customization/domain/models/CustomizationOptions';
import {
  normalizeSizeKey,
  type SizeOption,
} from '@/features/customization/utils/parseSizeList';
import type { PdpEddProductSpecs } from '../domain/models/ResolvedExpectedDeliveryDate';
import {
  formatEddDate,
  getCustomizationEddKeyFromSpecs,
  isSizeableSkuMap,
} from './eddFormatting';

function unwrapDesignEddDetails(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested = record.designEddDetails;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const nestedRecord = nested as Record<string, unknown>;
    const hasSkuMaps = Object.values(nestedRecord).some(
      (entry) =>
        Boolean(entry) &&
        typeof entry === 'object' &&
        'skuCodeMap' in (entry as Record<string, unknown>)
    );
    if (hasSkuMaps) return nestedRecord;
  }

  return record;
}

function parseSkuMapToSizeList(skuMap: Record<string, Record<string, unknown>>): SizeOptionWithEdd[] {
  const sizeList: SizeOptionWithEdd[] = [];

  Object.keys(skuMap)
    .sort()
    .forEach((mapKey) => {
      const entry = skuMap[mapKey];
      if (!entry || typeof entry !== 'object') return;

      const sizeKey = entry.size ?? mapKey;
      const displaySize = entry.displaySize ?? entry.size ?? mapKey;
      const deliveryDateInfo = entry.deliveryDateInfo as Record<string, unknown> | undefined;
      const expectedDeliveryDate =
        typeof deliveryDateInfo?.expectedDeliveryDate === 'string'
          ? deliveryDateInfo.expectedDeliveryDate
          : null;

      sizeList.push({
        key: String(sizeKey),
        value: String(displaySize),
        edd: expectedDeliveryDate ? formatEddDate(expectedDeliveryDate) : null,
        eddFullDate: expectedDeliveryDate,
      });
    });

  return sizeList;
}

function findSizeableCustomization(
  eddData: Record<string, unknown>,
  preferredKey: string,
): Record<string, unknown> | undefined {
  const preferred = eddData[preferredKey] as Record<string, unknown> | undefined;
  if (preferred?.skuCodeMap && isSizeableSkuMap(preferred.skuCodeMap as Record<string, unknown>)) {
    return preferred;
  }

  let best: Record<string, unknown> | undefined;
  let bestCount = 0;

  for (const entry of Object.values(eddData)) {
    if (!entry || typeof entry !== 'object') continue;
    const customization = entry as Record<string, unknown>;
    const skuMap = customization.skuCodeMap as Record<string, unknown> | undefined;
    if (!skuMap || !isSizeableSkuMap(skuMap)) continue;

    const count = Object.keys(skuMap).length;
    if (count > bestCount) {
      bestCount = count;
      best = customization;
    }
  }

  return best;
}

function mapFallbackSizes(fallbackSizes: SizeOption[]): SizeOptionWithEdd[] {
  return fallbackSizes.map((size) => ({
    key: String(size.key),
    value: String(size.value),
  }));
}

/**
 * Keep every unique size from EDD `skuCodeMap` and `customizationOptions.size`.
 * Men's bracelets often have only the default size in the product API (e.g. 8")
 * while EDD `skuCodeMap` has the full list (6", 6.5", 7", 7.5", 8").
 */
function mergeSizeLists(
  fallbackSizes: SizeOptionWithEdd[],
  eddSizes: SizeOptionWithEdd[],
): SizeOptionWithEdd[] {
  if (eddSizes.length === 0) return fallbackSizes;
  if (fallbackSizes.length === 0) return eddSizes;

  const eddByKey = new Map(
    eddSizes.map((size) => [normalizeSizeKey(size.key), size] as const)
  );

  const primary = eddSizes.length >= fallbackSizes.length ? eddSizes : fallbackSizes;
  const secondary = eddSizes.length >= fallbackSizes.length ? fallbackSizes : eddSizes;
  const seen = new Set<string>();
  const merged: SizeOptionWithEdd[] = [];

  const pushSize = (size: SizeOptionWithEdd) => {
    const key = normalizeSizeKey(size.key);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(eddByKey.get(key) ?? size);
  };

  primary.forEach(pushSize);
  secondary.forEach(pushSize);

  return merged;
}

/** Builds the PDP size list from EDD `skuCodeMap`, falling back to `customizationOptions.size`. */
export function parseEddSizeList(
  designEddDetails: unknown | null,
  productSpecs: PdpEddProductSpecs,
  fallbackSizes: SizeOption[],
): SizeOptionWithEdd[] {
  const fallback = mapFallbackSizes(fallbackSizes);
  const eddData = unwrapDesignEddDetails(designEddDetails);

  if (!eddData) {
    return fallback;
  }

  const preferredKey = getCustomizationEddKeyFromSpecs(productSpecs);
  const customization = findSizeableCustomization(eddData, preferredKey);

  if (!customization?.skuCodeMap) {
    return fallback;
  }

  const skuMap = customization.skuCodeMap as Record<string, Record<string, unknown>>;
  if (!isSizeableSkuMap(skuMap)) {
    return fallback;
  }

  const eddSizes = parseSkuMapToSizeList(skuMap);
  return mergeSizeLists(fallback, eddSizes);
}
